import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { useAction, useMutation, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { Id, Doc } from '../../convex/_generated/dataModel'
import { convertWorkflowMessages, type ChatMessage, type ChatAttachment } from '@/lib/chatMessages'

export const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp'])

export const MAX_IMAGE_DIMENSION = 4096;

export function resizeImage(file: File, opts?: { forceFormat?: string }): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => {
      let { naturalWidth: w, naturalHeight: h } = img
      const maxDim = Math.max(w, h)
      const needsResize = maxDim > MAX_IMAGE_DIMENSION
      const needsConvert = !!opts?.forceFormat

      if (!needsResize && !needsConvert) {
        URL.revokeObjectURL(img.src)
        resolve(file)
        return
      }

      if (needsResize) {
        const scale = MAX_IMAGE_DIMENSION / maxDim
        w = Math.round(w * scale)
        h = Math.round(h * scale)
      }

      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) { URL.revokeObjectURL(img.src); reject(new Error('Canvas not supported')); return }
      ctx.drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(img.src)

      const outType = opts?.forceFormat || file.type || 'image/jpeg'
      const quality = outType === 'image/jpeg' ? 0.9 : undefined
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Conversion failed')); return }
        const ext = outType === 'image/png' ? '.png' : outType === 'image/webp' ? '.webp' : '.jpg'
        const name = file.name.replace(/\.[^.]+$/, ext)
        resolve(new File([blob], name, { type: outType }))
      }, outType, quality)
    }
    img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error(`Cannot decode ${file.name}`)) }
    img.src = URL.createObjectURL(file)
  })
}

export function getAudioDurationFromFile(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio()
    audio.preload = 'metadata'
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src)
      resolve(audio.duration)
    }
    audio.onerror = () => {
      URL.revokeObjectURL(audio.src)
      reject(new Error(`Failed to load audio metadata for ${file.name}`))
    }
    audio.src = URL.createObjectURL(file)
  })
}

// Never rejects: resolves undefined when metadata can't be read or the
// reported duration is non-finite (e.g. streamed/fragmented files), so
// uploads proceed and the server keeps its existing fallback.
export function getVideoDurationFromFile(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src)
      const duration = video.duration
      resolve(Number.isFinite(duration) && duration > 0 ? duration : undefined)
    }
    video.onerror = () => {
      URL.revokeObjectURL(video.src)
      resolve(undefined)
    }
    video.src = URL.createObjectURL(file)
  })
}

export type {
  ToolCall,
  ImageAttachment,
  VideoAttachment,
  AudioAttachment,
  DocumentAttachment,
  ChatAttachment,
  ChatMessage,
} from '@/lib/chatMessages'

interface UseWorkflowChatOptions {
  workflowId: Id<'workflows'> | null
  userCredits?: number
  onToolComplete?: (toolName: string, result: unknown) => void
}

export function useWorkflowChat({ workflowId, onToolComplete }: UseWorkflowChatOptions) {
  const [optimisticUserMessage, setOptimisticUserMessage] = useState<ChatMessage | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const isSendingRef = useRef(false)
  const isCancellingRef = useRef(false)
  const queuedMessageRef = useRef<{ content: string; images?: File[]; audioFiles?: File[]; documents?: File[]; attachments?: ChatAttachment[] } | null>(null)
  const prevToolResultCountRef = useRef(0)
  const streamingSnapshotRef = useRef<{ content: string; toolCount: number; timestamp: number } | null>(null)

  const generateUploadUrl = useMutation(api.workflows.generateUploadUrl)
  const getStorageUrl = useMutation(api.workflows.getStorageUrl)
  const abortAllWorkflowStreamsMutation = useMutation(api.workflows.abortAllWorkflowStreams)
  const sendMessageMutation = useMutation(api.workflows.sendMessage)
  const processDocumentAction = useAction(api.documentProcessing.processDocument)

  // DB messages — the single source of truth
  const dbMessages = useQuery(
    api.workflows.getMessages,
    workflowId ? { workflowId } : 'skip'
  )

  const dbMessagesConverted = useMemo(
    (): ChatMessage[] => (dbMessages ? convertWorkflowMessages(dbMessages) : []),
    [dbMessages]
  )

  // Streaming state derived purely from DB
  const isDbStreaming = dbMessages?.some((m: Doc<'workflowMessages'>) => m.isStreaming) ?? false

  // Messages: DB + optimistic user message
  const assistantCount = dbMessagesConverted.filter(m => m.role === 'assistant').length
  useEffect(() => {
    if (assistantCount > 0) {
      console.log(`[chat] assistant messages in DB: ${assistantCount}, streaming: ${isDbStreaming}`)
    }
  }, [assistantCount, isDbStreaming])

  const messages = useMemo(() => {
    const result = [...dbMessagesConverted]

    if (optimisticUserMessage) {
      const userAlreadyInDb = dbMessagesConverted.some(
        m => m.role === 'user' && m.content === optimisticUserMessage.content
      )
      if (!userAlreadyInDb) {
        result.push(optimisticUserMessage)
      }
    }

    return result
  }, [dbMessagesConverted, optimisticUserMessage])

  // Clear optimistic user message when it appears in DB
  useEffect(() => {
    if (optimisticUserMessage && dbMessagesConverted.some(
      m => m.role === 'user' && m.content === optimisticUserMessage.content
    )) {
      setOptimisticUserMessage(null)
    }
  }, [dbMessagesConverted, optimisticUserMessage])

  // Auto-abort stuck streaming: if isStreaming but content/tools unchanged for 90s, abort
  useEffect(() => {
    if (!isDbStreaming || !workflowId || !dbMessages) {
      streamingSnapshotRef.current = null
      return
    }

    const streamingMsg = dbMessages.find((m: Doc<'workflowMessages'>) => m.isStreaming)
    if (!streamingMsg) return

    const currentContent = streamingMsg.content || ''
    const currentToolCount = (streamingMsg.toolResults?.length ?? 0) + (streamingMsg.toolCalls?.length ?? 0)
    const prev = streamingSnapshotRef.current

    if (!prev || prev.content !== currentContent || prev.toolCount !== currentToolCount) {
      streamingSnapshotRef.current = { content: currentContent, toolCount: currentToolCount, timestamp: Date.now() }
      return
    }

    const staleMs = Date.now() - prev.timestamp
    const STALE_TIMEOUT_MS = 180_000
    if (staleMs < STALE_TIMEOUT_MS) {
      const timer = setTimeout(() => {
        if (streamingSnapshotRef.current === prev) {
          console.warn('[useWorkflowChat] Streaming stale for 3m, auto-aborting')
          abortAllWorkflowStreamsMutation({ workflowId }).catch(console.error)
        }
      }, STALE_TIMEOUT_MS - staleMs)
      return () => clearTimeout(timer)
    }

    console.warn('[useWorkflowChat] Streaming stale for 3m, auto-aborting')
    abortAllWorkflowStreamsMutation({ workflowId }).catch(console.error)
  }, [isDbStreaming, dbMessages, workflowId, abortAllWorkflowStreamsMutation])

  // Fire onToolComplete reactively when new tool results appear in DB
  useEffect(() => {
    if (!onToolComplete || !dbMessages) return

    let totalToolResults = 0
    const newResults: Array<{ name: string; result: unknown }> = []

    for (const m of dbMessages) {
      if (m.toolResults) {
        for (const tr of m.toolResults) {
          totalToolResults++
          if (totalToolResults > prevToolResultCountRef.current) {
            const tc = m.toolCalls?.find((tc) => tc.id === tr.toolCallId)
            if (tc) {
              newResults.push({ name: tc.name, result: tr.result })
            }
          }
        }
      }
    }

    if (newResults.length > 0) {
      prevToolResultCountRef.current = totalToolResults
      for (const { name, result } of newResults) {
        onToolComplete(name, result)
      }
    }
  }, [dbMessages, onToolComplete])

  const uploadVisualFiles = useCallback(async (files: File[]): Promise<ChatAttachment[]> => {
    const attachments: ChatAttachment[] = []
    const total = files.length

    for (let i = 0; i < files.length; i++) {
      let file = files[i]
      const isVideo = file.type.startsWith('video/')
      const label = isVideo ? 'video' : 'image'
      setUploadStatus(total > 1 ? `Uploading ${label} ${i + 1} of ${total}...` : `Uploading ${file.name}...`)
      if (!isVideo && file.type.startsWith('image/')) {
        const forceFormat = !SUPPORTED_IMAGE_TYPES.has(file.type) ? 'image/png' : undefined
        file = await resizeImage(file, forceFormat ? { forceFormat } : undefined)
      }
      const uploadUrl = await generateUploadUrl()
      const result = await fetch(uploadUrl, {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': file.type },
      })

      if (!result.ok) {
        throw new Error(`Failed to upload ${file.name}`)
      }

      const { storageId } = await result.json()
      const url = await getStorageUrl({ storageId })

      if (url) {
        if (isVideo) {
          const duration = await getVideoDurationFromFile(file)
          attachments.push({ type: 'video', url, storageId, name: file.name, mimeType: file.type, duration })
        } else {
          attachments.push({ type: 'image', url, storageId, name: file.name })
        }
      }
    }

    return attachments
  }, [generateUploadUrl, getStorageUrl])

  const getAudioDuration = useCallback(getAudioDurationFromFile, [])

  const uploadAudioFiles = useCallback(async (files: File[]): Promise<ChatAttachment[]> => {
    const attachments: ChatAttachment[] = []
    const total = files.length

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      setUploadStatus(total > 1 ? `Uploading audio ${i + 1} of ${total}...` : `Uploading ${file.name}...`)
      if (file.size > 50 * 1024 * 1024) {
        throw new Error(`Audio file ${file.name} is too large (max 50MB)`)
      }

      let duration: number
      try {
        duration = await getAudioDuration(file)
      } catch {
        throw new Error(`Could not read audio file ${file.name}`)
      }

      if (duration < 0.5) {
        throw new Error(`Audio file ${file.name} is too short (min 0.5s)`)
      }
      if (duration > 300) {
        throw new Error(`Audio file ${file.name} is too long (max 5 minutes)`)
      }

      const uploadUrl = await generateUploadUrl()
      const result = await fetch(uploadUrl, {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': file.type },
      })

      if (!result.ok) {
        throw new Error(`Failed to upload ${file.name}`)
      }

      const { storageId } = await result.json()
      const url = await getStorageUrl({ storageId })

      if (url) {
        attachments.push({
          type: 'audio',
          url,
          storageId,
          name: file.name,
          duration,
          mimeType: file.type,
        })
      }
    }

    return attachments
  }, [generateUploadUrl, getStorageUrl, getAudioDuration])

  const uploadDocuments = useCallback(async (files: File[]): Promise<ChatAttachment[]> => {
    const attachments: ChatAttachment[] = []
    const total = files.length

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (file.size > 100 * 1024 * 1024) {
        throw new Error(`Document ${file.name} is too large (max 100MB)`)
      }

      setUploadStatus(total > 1 ? `Uploading document ${i + 1} of ${total}...` : `Uploading ${file.name}...`)
      const uploadUrl = await generateUploadUrl()
      const result = await fetch(uploadUrl, {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': file.type },
      })

      if (!result.ok) {
        throw new Error(`Failed to upload ${file.name}`)
      }

      const { storageId } = await result.json()
      const url = await getStorageUrl({ storageId })

      if (!url) continue

      let extractedText: string | undefined
      let pageCount: number | undefined
      try {
        setUploadStatus(`Parsing ${file.name}...`)
        const extracted = await processDocumentAction({
          storageId,
          filename: file.name,
          mimeType: file.type,
        })
        extractedText = extracted.extractedText
        pageCount = extracted.pageCount
      } catch (e) {
        console.warn(`Document text extraction failed for ${file.name}:`, e)
      }

      attachments.push({
        type: 'document',
        url,
        storageId,
        name: file.name,
        mimeType: file.type,
        extractedText,
        pageCount,
      })
    }

    return attachments
  }, [generateUploadUrl, getStorageUrl, processDocumentAction])

  const sendMessage = useCallback(async (
    content: string,
    opts?: { images?: File[]; audioFiles?: File[]; documents?: File[]; attachments?: ChatAttachment[] }
  ) => {
    if (!workflowId || isUploading) {
      return
    }

    // Queue if already sending
    if (isSendingRef.current) {
      queuedMessageRef.current = { content, images: opts?.images, audioFiles: opts?.audioFiles, documents: opts?.documents, attachments: opts?.attachments }
      return
    }

    // If there's a stuck streaming message in DB, abort it first
    if (isDbStreaming) {
      try {
        await abortAllWorkflowStreamsMutation({ workflowId })
      } catch (e) {
        console.error('Failed to abort stuck stream:', e)
      }
    }

    isSendingRef.current = true
    setError(null)

    // Upload media files
    let attachments: ChatAttachment[] | undefined
    const hasImages = opts?.images && opts.images.length > 0
    const hasAudio = opts?.audioFiles && opts.audioFiles.length > 0
    const hasDocs = opts?.documents && opts.documents.length > 0
    const directAttachments = opts?.attachments && opts.attachments.length > 0 ? opts.attachments : undefined

    if (hasImages || hasAudio || hasDocs) {
      setIsUploading(true)
      try {
        attachments = []
        if (hasImages) {
          const imageAttachments = await uploadVisualFiles(opts!.images!)
          attachments.push(...imageAttachments)
        }
        if (hasAudio) {
          const audioAttachments = await uploadAudioFiles(opts!.audioFiles!)
          attachments.push(...audioAttachments)
        }
        if (hasDocs) {
          const docAttachments = await uploadDocuments(opts!.documents!)
          attachments.push(...docAttachments)
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to upload files')
        setIsUploading(false)
        setUploadStatus(null)
        isSendingRef.current = false
        return
      }
      setIsUploading(false)
      setUploadStatus(null)
    }

    if (directAttachments) {
      attachments = [...(attachments ?? []), ...directAttachments]
    }

    // Show user message immediately (optimistic)
    setOptimisticUserMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: Date.now(),
      attachments,
    })

    try {
      await sendMessageMutation({ workflowId, content, attachments })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send message')
    } finally {
      isSendingRef.current = false
    }
  }, [workflowId, isUploading, isDbStreaming, sendMessageMutation, uploadVisualFiles, uploadAudioFiles, uploadDocuments, abortAllWorkflowStreamsMutation])

  // Process queued message when streaming ends
  useEffect(() => {
    if (!isDbStreaming && !isSendingRef.current && queuedMessageRef.current) {
      const { content, images, audioFiles, documents, attachments } = queuedMessageRef.current
      queuedMessageRef.current = null
      setTimeout(() => {
        sendMessage(content, { images, audioFiles, documents, attachments })
      }, 0)
    }
  }, [isDbStreaming, sendMessage])

  // Cancel streaming — just set abort flag in DB
  const cancelStream = useCallback(async () => {
    if (isCancellingRef.current || !workflowId) return
    isCancellingRef.current = true

    try {
      await abortAllWorkflowStreamsMutation({ workflowId })
    } catch (e) {
      console.error('Failed to abort workflow streams:', e)
    }

    isCancellingRef.current = false
  }, [workflowId, abortAllWorkflowStreamsMutation])

  // Allow parent to set an optimistic message early (e.g. during welcome→editor transition)
  const setPendingMessage = useCallback((content: string, attachments?: ChatAttachment[]) => {
    setOptimisticUserMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: Date.now(),
      attachments,
    })
  }, [])

  return {
    messages,
    isStreaming: isDbStreaming,
    isUploading,
    uploadStatus,
    error,
    sendMessage,
    cancelStream,
    hasLoaded: dbMessages !== undefined,
    setPendingMessage,
  }
}
